import React, { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';
import { Card, CardHeader } from '../components/UI/Card';
import Button from '../components/UI/Button';
import { Input } from '../components/UI/Input';
import { isVip } from '../utils/segments';
import { smsInfo } from '../utils/sms';
import GenieTip from '../components/GenieTip';
import './Messages.css';

/**
 * 💬 Messages (Lot B) — SMS + WhatsApp réunis en un seul endroit.
 * V1 : composer un SMS (contact ou numéro libre) + historique des envois.
 * Futur foyer de l'inbox (pari 2 du blueprint).
 */

// Miroir de config/appee.php sms_packs — l'ordre = l'index envoyé au checkout
const SMS_PACKS = [
  { credits: 50,   price: 9 },
  { credits: 100,  price: 16 },
  { credits: 500,  price: 69 },
  { credits: 1000, price: 119 },
];

const STATUS_META = {
  sent:      { label: 'Envoyé',    color: '#10B981', bg: '#ECFDF5' },
  delivered: { label: 'Délivré',   color: '#10B981', bg: '#ECFDF5' },
  failed:    { label: 'Échec',     color: '#EF4444', bg: '#FEF2F2' },
  pending:   { label: 'En cours',  color: '#F59E0B', bg: '#FFFBEB' },
};

function StatusPill({ status, delivery }) {
  const key  = delivery === 'delivered' ? 'delivered' : status;
  const meta = STATUS_META[key] || STATUS_META.pending;
  return (
    <span className="msg-pill" style={{ color: meta.color, background: meta.bg }}>
      {meta.label}
    </span>
  );
}

export default function Messages() {
  const { contacts, tenant, addNotification, showQuotaError } = useApp();
  const [tab, setTab] = useState('sms');

  // ── Composer ──
  const [search, setSearch]   = useState('');
  const [selected, setSelected] = useState(null);
  const [group, setGroup]     = useState(null);   // { key, label, contacts }
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // ── Historique ──
  const [logs, setLogs] = useState(null);
  const [waLogs, setWaLogs] = useState(null);
  const [logsError, setLogsError] = useState(false);
  const [smsPage, setSmsPage]         = useState(0);
  const [smsPageSize, setSmsPageSize] = useState(50);
  const [openDays, setOpenDays]       = useState(() => new Set());

  const credits   = tenant?.sms_credits ?? 0;

  const loadLogs = () => {
    api.getSmsLogs().then(r => setLogs(r.logs ?? r ?? [])).catch(() => setLogsError(true));
    api.getWhatsAppLogs().then(r => setWaLogs(r.logs ?? r ?? [])).catch(() => setWaLogs([]));
  };
  useEffect(loadLogs, []);

  // Retour d'un achat de pack initié ici
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('billing') === 'success') {
      addNotification('✅ Paiement confirmé ! Tes crédits sont ajoutés.');
      window.history.replaceState({}, '', '/messages');
    } else if (q.get('billing') === 'cancel') {
      addNotification("Paiement annulé — rien n'a été débité.", 'info');
      window.history.replaceState({}, '', '/messages');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [buying, setBuying] = useState(false);
  const buyPack = async (idx) => {
    setBuying(true);
    try {
      const res = await api.checkoutPack('sms', idx, '/messages');
      if (res?.url) { window.location.href = res.url; return; }
      addNotification('Paiement indisponible pour le moment.', 'error');
    } catch (e) {
      addNotification(e.message || 'Paiement indisponible pour le moment.', 'error');
    } finally {
      setBuying(false);
    }
  };

  // L'app propose AVANT qu'on cherche : les groupes qui ont du sens aujourd'hui.
  const smartGroups = useMemo(() => {
    const today = new Date();
    const withPhone = contacts.filter(c => c.phone && !c.sms_opted_out && c.sms_consent !== false && c.status === 'active');
    const isBdayToday = c => {
      if (!c.birth_date) return false;
      const b = new Date(c.birth_date);
      return b.getDate() === today.getDate() && b.getMonth() === today.getMonth();
    };
    const daysSince = c => c.last_interaction
      ? Math.floor((today - new Date(c.last_interaction)) / 86400000)
      : Infinity;
    const groups = [
      { key: 'bday',    icon: '🎂', label: "Anniversaires aujourd'hui", contacts: withPhone.filter(isBdayToday),
        suggest: `Joyeux anniversaire {{prénom}} ! 🎂 Toute l'équipe ${tenant?.name ?? ''} te souhaite une magnifique journée.` },
      { key: 'vip',     icon: '🏆', label: 'Mes VIP',                   contacts: withPhone.filter(isVip),
        suggest: `{{prénom}}, merci d'être un client en or 💛 Passe quand tu veux, on a toujours une attention pour toi.` },
      { key: 'dormant', icon: '💤', label: 'Silencieux depuis 6 mois',  contacts: withPhone.filter(c => daysSince(c) > 180),
        suggest: `{{prénom}}, ça fait un moment ! On pense à toi — passe nous voir, une surprise t'attend. 😊` },
      { key: 'recent',  icon: '🕐', label: 'Contactés récemment',       contacts: withPhone.filter(c => daysSince(c) <= 30),
        suggest: `{{prénom}}, merci pour ta visite ! Au plaisir de te revoir très vite.` },
    ];
    return groups.filter(g => g.contacts.length > 0);
  }, [contacts, tenant]);

  const pickGroup = (g) => {
    setGroup({ key: g.key, label: g.label, icon: g.icon, contacts: g.contacts });
    setSelected(null); setSearch('');
    if (!message.trim()) setMessage(g.suggest);
  };

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || selected) return [];
    return contacts
      .filter(c => c.phone && !c.sms_opted_out)
      .filter(c => `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) || (c.phone || '').includes(q))
      .slice(0, 6);
  }, [search, contacts, selected]);

  const manualPhone = !selected && /^[+\d\s]{6,}$/.test(search.trim()) ? search.trim() : null;
  const recipient   = selected || (manualPhone ? { phone: manualPhone, first_name: manualPhone } : null);
  const nbTargets   = group ? group.contacts.length : (recipient ? 1 : 0);
  const canSend     = nbTargets > 0 && message.trim().length > 0 && credits >= nbTargets && !sending;

  const personalize = (text, c) => text
    .replaceAll('{{prénom}}', c.first_name || '')
    .replaceAll('{{nom}}', c.last_name || '');

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      if (group) {
        await api.sendSmsBulk({
          contacts: group.contacts.map(c => ({ id: c.id, phone: c.phone })),
          message: message.trim(),
        });
        addNotification(`📱 ${group.contacts.length} SMS en route vers « ${group.label} »`);
      } else {
        await api.sendSms({ phone: recipient.phone, message: personalize(message.trim(), recipient), contact_id: recipient.id ?? null });
        addNotification(`📱 SMS envoyé à ${recipient.first_name}`);
      }
      setMessage(''); setSelected(null); setGroup(null); setSearch('');
      loadLogs();
    } catch (err) {
      if (err.quotaExceeded) showQuotaError(err);
      else addNotification(err.message || "L'envoi a échoué — réessaie.", 'error');
    } finally {
      setSending(false);
    }
  };

  const activeLogs = tab === 'sms' ? logs : waLogs;

  return (
    <div className="messages-page">

      {/* ── Hero ── */}
      <div className="msg-hero">
        <div className="msg-hero-nebula" />
        <div className="msg-hero-inner">
          <div className="msg-hero-left">
            <div className="msg-hero-icon-wrap">💬</div>
            <div>
              <h1 className="msg-hero-title">Messages</h1>
              <p className="msg-hero-sub">Écris à tes clients par SMS, au bon moment</p>
            </div>
          </div>
          <div className="msg-hero-chips">
            <span className="msg-hero-chip">📱 {credits} crédit{credits !== 1 ? 's' : ''} SMS</span>
          </div>
        </div>
      </div>

      {/* ── Aide Génie (première visite) ── */}
      <GenieTip
        id="sms-compose"
        title="👋 Première fois ? Envoyer un SMS, c'est 3 étapes"
        steps={[
          <>Clique un groupe ci-dessous (ex. <b>Anniversaires aujourd'hui</b>) — je remplis les destinataires pour toi.</>,
          <>Écris ton message. Astuce : tape <b>{'{{prénom}}'}</b> et chacun reçoit son propre prénom.</>,
          <>Clique <b>Envoyer</b>. Un SMS = 1 crédit ({credits} restant{credits !== 1 ? 's' : ''} ce mois).</>,
        ]}
        foot={<>📱 Le SMS est lu à <b>~98 %</b> — parfait pour un rappel ou une offre courte. Tes clients voient <b>ton entreprise</b> comme expéditeur.</>}
      />

      {/* ── Composer SMS ── */}
      <Card>
        <CardHeader title="✍️ Envoyer un SMS" />
        <div className="msg-compose">

          {smartGroups.length > 0 && !group && !selected && (
            <div className="msg-smart">
              <span className="msg-smart-label">✨ Appee te propose :</span>
              <div className="msg-smart-chips">
                {smartGroups.map(g => (
                  <button key={g.key} className="msg-smart-chip" onClick={() => pickGroup(g)}>
                    {g.icon} {g.label} <b>{g.contacts.length}</b>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="msg-compose-to">
            {group ? (
              <div className="msg-selected msg-selected-group">
                <span>{group.icon} {group.label} · <b>{group.contacts.length} destinataire{group.contacts.length > 1 ? 's' : ''}</b> ({group.contacts.length} crédit{group.contacts.length > 1 ? 's' : ''})</span>
                <button onClick={() => { setGroup(null); setMessage(''); }} aria-label="Retirer le groupe">✕</button>
              </div>
            ) : selected ? (
              <div className="msg-selected">
                <span>📱 {selected.first_name} {selected.last_name} · {selected.phone}</span>
                <button onClick={() => { setSelected(null); setSearch(''); }} aria-label="Retirer le destinataire">✕</button>
              </div>
            ) : (
              <Input
                label="Destinataire"
                placeholder="🔍 Recherche par nom, ou tape un numéro (+41…)"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            )}
            {matches.length > 0 && (
              <div className="msg-suggest">
                {matches.map(c => (
                  <button key={c.id} onClick={() => setSelected(c)}>
                    {c.first_name} {c.last_name} <span>{c.phone}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="msg-compose-body">
            <label className="msg-label">Message</label>
            <textarea
              className="msg-textarea"
              rows={3}
              maxLength={320}
              placeholder="Ton message…"
              value={message}
              onChange={e => setMessage(e.target.value)}
            />
            {group && (
              <p className="msg-vars-hint">💡 {'{{prénom}}'} sera remplacé par le prénom de chaque destinataire.</p>
            )}
            <div className="msg-compose-foot">
              <span className="msg-count">
                {smsInfo(message).chars} car. · {smsInfo(message).segments} SMS{smsInfo(message).unicode ? ' · emoji ⚠️' : ''}
                {group ? ` × ${group.contacts.length}` : ''}
              </span>
              <Button loading={sending} disabled={!canSend} onClick={handleSend}>
                📤 Envoyer
              </Button>
            </div>
            {(credits < nbTargets || credits === 0) && (() => {
              const missing = Math.max(nbTargets - credits, 1);
              const recIdx  = SMS_PACKS.findIndex(pk => pk.credits >= missing);
              const rec     = recIdx === -1 ? SMS_PACKS.length - 1 : recIdx;
              return (
                <div className="msg-buy">
                  <div className="msg-buy-head">
                    ⚡ {credits === 0
                      ? 'Plus de crédits SMS — recharge en 1 clic :'
                      : `Il te manque ${missing} crédit${missing > 1 ? 's' : ''} pour ce groupe — ajoute un pack en 1 clic :`}
                  </div>
                  <div className="msg-buy-packs">
                    {SMS_PACKS.map((pk, idx) => (
                      <button
                        key={pk.credits}
                        className={`msg-buy-pack ${idx === rec ? 'rec' : ''}`}
                        disabled={buying}
                        onClick={() => buyPack(idx)}
                      >
                        {idx === rec && <span className="msg-buy-rec">Recommandé</span>}
                        <b>{pk.credits} SMS</b>
                        <span>CHF {pk.price}</span>
                      </button>
                    ))}
                  </div>
                  <p className="msg-buy-note">Paiement sécurisé Stripe · les crédits ne périment jamais · tu reviens directement ici.</p>
                </div>
              );
            })()}
          </div>
        </div>
      </Card>

      {/* ── Historique ── */}
      <Card>
        <div className="msg-tabs">
          <button className="active">📱 Historique SMS</button>
        </div>

        {logsError && tab === 'sms' && (
          <div className="msg-error">
            Impossible de charger l'historique.
            <button onClick={() => { setLogsError(false); loadLogs(); }}>Réessayer</button>
          </div>
        )}

        {activeLogs === null && !logsError && <p className="msg-loading">Chargement…</p>}

        {Array.isArray(activeLogs) && activeLogs.length === 0 && (
          <div className="msg-empty">
            📭 Aucun SMS envoyé pour le moment — ton premier partira d'ici ou d'une campagne.
          </div>
        )}

        {Array.isArray(activeLogs) && activeLogs.length > 0 && (() => {
          const sorted = [...activeLogs].sort((a, b) => new Date(b.sent_at || 0) - new Date(a.sent_at || 0));
          const total  = sorted.length;
          const start  = smsPage * smsPageSize;
          const visible = sorted.slice(start, start + smsPageSize);

          // Regroupement mois → jour
          const months = []; const mMap = {};
          visible.forEach(l => {
            const d = l.sent_at ? new Date(l.sent_at) : null;
            const mKey = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : 'na';
            const dKey = d ? `${mKey}-${String(d.getDate()).padStart(2, '0')}` : 'na';
            if (!mMap[mKey]) { mMap[mKey] = { key: mKey, label: d ? d.toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' }) : 'Sans date', days: {}, order: [] }; months.push(mMap[mKey]); }
            const M = mMap[mKey];
            if (!M.days[dKey]) { M.days[dKey] = { key: dKey, label: d ? d.toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Sans date', items: [] }; M.order.push(dKey); }
            M.days[dKey].items.push(l);
          });
          const firstDayKey = months[0]?.order[0]; // le jour le plus récent est ouvert par défaut
          const isOpen = (dk) => openDays.has(dk) || (openDays.size === 0 && dk === firstDayKey);
          const toggleDay = (dk) => setOpenDays(s => { const n = new Set(s.size === 0 && firstDayKey ? [firstDayKey] : s); n.has(dk) ? n.delete(dk) : n.add(dk); return n; });

          return (
            <div className="msg-sms-history">
              {months.map(M => (
                <div key={M.key} className="msg-month">
                  <div className="msg-month-sep"><span>{M.label}</span></div>
                  {M.order.map(dk => {
                    const day = M.days[dk]; const open = isOpen(dk);
                    return (
                      <div key={dk} className="msg-day">
                        <button className={`msg-day-head${open ? ' open' : ''}`} onClick={() => toggleDay(dk)}>
                          <span className="msg-day-caret">{open ? '▾' : '▸'}</span>
                          <span className="msg-day-label">{day.label}</span>
                          <span className="msg-day-count">{day.items.length} SMS</span>
                        </button>
                        {open && (
                          <div className="msg-table-wrap">
                            <table className="msg-table">
                              <tbody>
                                {day.items.map(l => (
                                  <tr key={l.id}>
                                    <td className="msg-td-phone">{l.phone}</td>
                                    <td className="msg-td-msg">{(l.message || '').slice(0, 80)}{(l.message || '').length > 80 ? '…' : ''}</td>
                                    <td><StatusPill status={l.status} delivery={l.delivery_status} /></td>
                                    <td className="msg-td-date">{l.sent_at ? new Date(l.sent_at).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}

              <div className="msg-pager">
                <div className="msg-pager-size">
                  <span>Afficher</span>
                  {[20, 50, 100].map(n => (
                    <button key={n} className={smsPageSize === n ? 'active' : ''} onClick={() => { setSmsPageSize(n); setSmsPage(0); }}>{n}</button>
                  ))}
                </div>
                <div className="msg-pager-nav">
                  <span>{total === 0 ? 0 : start + 1}–{Math.min(start + smsPageSize, total)} sur {total}</span>
                  <button disabled={smsPage === 0} onClick={() => setSmsPage(p => Math.max(0, p - 1))}>‹</button>
                  <button disabled={start + smsPageSize >= total} onClick={() => setSmsPage(p => p + 1)}>›</button>
                </div>
              </div>
            </div>
          );
        })()}
      </Card>
    </div>
  );
}
